import * as Sentry from '@sentry/react';
import { createContext, useEffect, useState } from 'react';
import {
  SIDENAV_MENU_CHATBOT,
  SIDENAV_MENUS
} from '../../Container/SidebarV1/constants';
import { getCurrentUser } from '../../Redux/util/Apis/users';
import { deleteCookie } from '..';

export const GlobalContext = createContext({});

const GlobalContextProvider = ({ children }) => {
  const [sideNavMenu, setSideNavMenu] = useState([]);
  const [leadData, setLeadData] = useState({});
  const [isZumaUser, setIsZumaUser] = useState(false);
  const [unit, setUnit] = useState({});
  const [user, setUser] = useState(null);

  const themePreference = localStorage.getItem('theme_preference')
    ? JSON.parse(localStorage.getItem('theme_preference'))
    : false;
  const [darkMode, setDarkMode] = useState(themePreference);

  const fetchUserData = async () => {
    const result = await getCurrentUser();
    if (result?.error) {
      if (result.status === 401) {
        deleteCookie('rb_access_token');
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== '/login') {
          sessionStorage.setItem('post_login_redirect', currentPath);
        }
        window.location.href = '/login';
      }
      return;
    }
    setUser(result.data);
    const roleName = result.data?.role?.name;
    setIsZumaUser(roleName !== 'CLIENT_ADMIN' && roleName !== 'CLIENT_USER');
  };

  useEffect(() => {
    if (!user) return;

    if (user.role?.permissions?.length) {
      const permissionNames = user.role.permissions.map(p => p.name);
      const resp = [];
      for (let key in SIDENAV_MENUS) {
        if (permissionNames.includes(key)) {
          resp.push(SIDENAV_MENUS[key]);
        }
      }
      if (user.role.name === 'SUPER_ADMIN') {
        resp.push(SIDENAV_MENU_CHATBOT);
      }

      setSideNavMenu(resp);
    }

    if (
      user.role?.name === 'CLIENT_USER' ||
      user.role?.name === 'CLIENT_ADMIN'
    ) {
      setIsZumaUser(false);
    } else {
      setIsZumaUser(true);
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('theme_preference', JSON.stringify(darkMode));
  }, []);

  // REST uses `id`; downstream consumers of `userData` read `_id`. Remap deeply
  // so we don't have to touch every downstream callsite.
  const remapId = node => {
    if (Array.isArray(node)) return node.map(remapId);
    if (node && typeof node === 'object' && node.constructor === Object) {
      const out = {};
      for (const [k, v] of Object.entries(node)) {
        out[k === 'id' ? '_id' : k] = remapId(v);
      }
      return out;
    }
    return node;
  };

  // Map REST fields back to the legacy GraphQL-shaped `userData` so downstream
  // consumers (Communities.js, etc.) keep working unchanged.
  const userData = {
    _id: user?.id,
    name: user?.name,
    trainedClients: remapId(user?.trainedClients),
    clients: remapId(user?.trainedClients),
    assignedClients: remapId(user?.assignedClients),
    mightyCall: user?.mightyCall,
    user_id: user?.uid,
    email: user?.email,
    tenantId: remapId(user?.userAccess?.clients?.trained) || [],
    userAccess: remapId(user?.userAccess),
    role: remapId(user?.role),
    status: user?.status
  };

  process.env.NODE_ENV === 'production' &&
    userData?.email &&
    Sentry.setUser({
      email: userData.email,
      username: userData.name,
      id: userData._id
    });

  const updateLeadData = data => {
    setLeadData(data);
  };

  const toggleTheme = () => {
    setDarkMode(prevState => {
      localStorage.setItem('theme_preference', JSON.stringify(!prevState));
      return !prevState;
    });
  };

  const value = {
    sideNavMenu,
    sideNavRoutes: sideNavMenu.map(menu => menu.access),
    userData,
    leadData,
    fetchUserData,
    isZumaUser,
    updateLeadData,
    darkMode,
    toggleTheme,
    unit,
    setUnit
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

export default GlobalContextProvider;
